import { useContext, useState } from "react";
import { Button, Input } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useBillingAdminText } from "../billingAdminI18n";

const formatDate = (value, lang) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(value));
};

const toDateInputValue = (value) => (value ? new Date(value).toISOString().slice(0, 10) : "");

// Generic extend-coverage flow: works for a single branch (branches.length === 1)
// or a restaurant-wide "extend all" (branches.length > 1). Shows the current end
// date(s) and the chosen new end date before the admin confirms.
const ExtendCoverageModal = ({ title, description, branches = [], onCancel, onConfirm }) => {
  const { lang } = useContext(Context);
  const text = useBillingAdminText(lang);
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const latestCurrentEnd = branches.reduce((max, branch) => {
    if (!branch.currentEndDate) return max;
    const value = new Date(branch.currentEndDate);
    return !max || value > max ? value : max;
  }, null);
  const floor = latestCurrentEnd || new Date();

  const confirm = async () => {
    setError("");
    if (!endDate) {
      setError(text.subscriptions.selectEndDate);
      return;
    }
    const parsed = new Date(endDate);
    if (parsed <= floor) {
      setError(text.subscriptions.endDateBeforeCurrent);
      return;
    }
    setSaving(true);
    try {
      await onConfirm(endDate);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="billing-modal-backdrop">
      <div className="billing-modal">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
        {error && <div className="billing-error">{error}</div>}
        <div className="billing-branch-list">
          {branches.map((branch) => (
            <div key={branch.branchId || branch.name} className="billing-branch-option billing-sub-branch-row">
              <span>{branch.name}</span>
              <small>
                {text.subscriptions.currentEndDate}: {formatDate(branch.currentEndDate, lang)}
              </small>
            </div>
          ))}
        </div>
        <Input
          type="date"
          label={text.subscriptions.newEndDate}
          value={endDate}
          min={toDateInputValue(floor)}
          onChange={(event) => setEndDate(event.target.value)}
        />
        {endDate && (
          <div className="billing-preview-box">
            <div>
              <span>{text.subscriptions.currentEndDate}</span>
              <strong>{formatDate(latestCurrentEnd, lang)}</strong>
            </div>
            <div>
              <span>{text.subscriptions.newEndDate}</span>
              <strong>{formatDate(endDate, lang)}</strong>
            </div>
          </div>
        )}
        <div className="billing-modal-actions">
          <Button appearance="filled" loading={saving} onClick={confirm}>
            {text.subscriptions.confirmExtend}
          </Button>
          <Button appearance="ghost" onClick={onCancel}>
            {text.common.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExtendCoverageModal;
